"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Lock } from "lucide-react";
import { useId, useState } from "react";

import { Button } from "@/nowadays-ui/components/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/nowadays-ui/components/input-group";
import { NowadaysUiScope } from "@/nowadays-ui/NowadaysUiScope";

import type { Gate } from "../../_logic/public/gate";
import { EASE_OUT } from "../../_logic/motion";

interface EstimateLockOverlayProps {
  gate: Exclude<Gate, { status: "preview" }>;
  emailInput: React.RefObject<HTMLInputElement>;
  onSubmit: (email: string) => void;
}

/**
 * The ask over the blurred results once a visitor reaches for the sample: a lock, one line, and
 * an email field with Get started inside it. A rejected or failed email says why under it.
 * @param props.gate - The locked gate: waiting, sending, or failed with a message.
 * @param props.emailInput - Ref for the email field, so the gate can focus it.
 * @param props.onSubmit - Called with what was typed.
 * @returns The overlay.
 * @throws Never.
 */
export const EstimateLockOverlay = ({ gate, emailInput, onSubmit }: EstimateLockOverlayProps) => {
  // Hooks
  const reduceMotion = useReducedMotion();
  const errorId = useId();

  // States
  const [email, setEmail] = useState(gate.status === "locked" ? "" : gate.email);

  // Derived
  const error = gate.status === "error" ? gate.message : null;

  // Handlers
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(email);
  };

  // Render
  return (
    <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/30 pt-10 sm:px-4 md:pt-16">
      <NowadaysUiScope className="w-full max-w-md">
        <motion.div
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: EASE_OUT }}
          role="dialog"
          aria-labelledby={`${errorId}-title`}
          className="flex flex-col items-center gap-5 rounded-3xl border border-gray-200 bg-white px-3 py-6 text-center sm:px-6 shadow-[0_2px_12px_-2px_oklch(0.4_0.02_280/0.1)] md:p-8"
        >
          <span className="flex size-10 items-center justify-center rounded-full bg-gray-100">
            <Lock aria-hidden className="size-4 text-gray-700" />
          </span>
          <div className="flex flex-col gap-1.5">
            <h2 id={`${errorId}-title`} className="text-lg font-semibold tracking-tight text-gray-900">
              Enter your email to see your estimate
            </h2>
            <p className="text-balance text-sm font-medium text-gray-600">
              Price your own trip with live fares and hotel rates. Free to start.
            </p>
          </div>
          <form noValidate onSubmit={handleSubmit} className="flex w-full flex-col gap-2">
            <InputGroup className="h-12 border-gray-200 bg-white pr-1.5 shadow-sm">
              <InputGroupInput
                ref={emailInput}
                autoFocus
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder="What's your email?"
                aria-label="Email"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="pl-3 sm:pl-4"
              />
              <InputGroupAddon align="inline-end" className="py-0">
                <Button type="submit" isPending={gate.status === "submitting"}>
                  Get started
                </Button>
              </InputGroupAddon>
            </InputGroup>
            {error && (
              <p id={errorId} role="alert" className="px-4 text-left text-sm font-medium text-red-600">
                {error}
              </p>
            )}
          </form>
        </motion.div>
      </NowadaysUiScope>
    </div>
  );
};
